import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ProductFilters, ProductSort } from '@/types/catalogue';

const SORTS: ProductSort[] = [
  'newest',
  'oldest',
  'price_asc',
  'price_desc',
  'name_asc',
  'name_desc',
  'featured',
];

/**
 * Keeps catalogue filters in the URL — BUILD-PLAN §9, §39, §48.
 *
 * The URL is the single source of truth rather than component state. That
 * makes a filtered view shareable, bookmarkable and back-button correct, and
 * it means the browser's Back button steps through filter changes the way
 * customers expect. Holding filters in useState would break all three.
 */
export function useProductFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<ProductFilters>(() => {
    const list = (key: string): string[] => {
      const raw = searchParams.get(key);

      return raw ? raw.split(',').filter(Boolean) : [];
    };

    const number = (key: string): number | undefined => {
      const raw = searchParams.get(key);
      if (raw === null || raw === '') return undefined;
      const parsed = Number(raw);

      return Number.isFinite(parsed) ? parsed : undefined;
    };

    const sortParam = searchParams.get('sort');

    return {
      search: searchParams.get('search') ?? undefined,
      categories: list('categories'),
      occasions: list('occasions'),
      collections: list('collections'),
      min_price: number('min_price'),
      max_price: number('max_price'),
      personalisable: searchParams.get('personalisable') === '1' ? true : undefined,
      in_stock: searchParams.get('in_stock') === '1' ? true : undefined,
      // Guard against a hand-edited or stale URL: an unrecognised sort falls
      // back rather than sending a value the API will reject with a 422.
      sort: sortParam && SORTS.includes(sortParam as ProductSort)
        ? (sortParam as ProductSort)
        : undefined,
      page: number('page'),
    };
  }, [searchParams]);

  /**
   * Applies a partial filter change.
   *
   * Always resets to page 1 unless the change *is* a page change — staying
   * on page 4 after narrowing a filter usually lands on an empty page, which
   * reads as "no results" when results exist.
   */
  const setFilter = useCallback(
    (updates: Partial<ProductFilters>) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);

          for (const [key, value] of Object.entries(updates)) {
            const isEmpty =
              value === undefined ||
              value === null ||
              value === '' ||
              value === false ||
              (Array.isArray(value) && value.length === 0);

            if (isEmpty) {
              next.delete(key);
              continue;
            }

            if (Array.isArray(value)) {
              next.set(key, value.join(','));
            } else if (typeof value === 'boolean') {
              next.set(key, '1');
            } else {
              next.set(key, String(value));
            }
          }

          if (!('page' in updates)) {
            next.delete('page');
          }

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  /** Toggles one slug within a multi-select facet. */
  const toggleTerm = useCallback(
    (key: 'categories' | 'occasions' | 'collections', slug: string) => {
      const current = filters[key] ?? [];

      setFilter({
        [key]: current.includes(slug)
          ? current.filter((item) => item !== slug)
          : [...current, slug],
      });
    },
    [filters, setFilter],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  /** Whether anything narrows the catalogue — drives the empty-state copy. */
  const hasFilters = useMemo(
    () =>
      Boolean(
        filters.search ||
          filters.categories?.length ||
          filters.occasions?.length ||
          filters.collections?.length ||
          filters.min_price !== undefined ||
          filters.max_price !== undefined ||
          filters.personalisable ||
          filters.in_stock,
      ),
    [filters],
  );

  return { filters, setFilter, toggleTerm, clearFilters, hasFilters };
}
